import { motion } from 'motion/react';
import { Link } from 'react-router-dom';
import { Container, Kicker } from '@/components/primitives';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';

const FOUNDERS = [
  {
    name: 'Dante Marramiero',
    role: 'Founder · CEO',
    photo: '/team/dante-marramiero.jpg',
    photoSize: '155%',
    photoPosition: '50% 18%',
    linkedin: 'https://www.linkedin.com/in/dante-marramiero/',
    gradient: 'conic-gradient(from 140deg, oklch(0.66 0.15 245), oklch(0.45 0.15 255), oklch(0.78 0.11 195))',
    education: [
      { name: 'Università Bocconi', crest: '/team/crests/bocconi.png' },
      { name: 'University of Cambridge', crest: '/team/crests/cambridge.webp' },
    ],
  },
  {
    name: 'Matteo Angelozzi',
    role: 'Co-founder · CPO',
    photo: '/team/matteo-angelozzi.jpg',
    photoSize: undefined as string | undefined,
    photoPosition: undefined as string | undefined,
    linkedin: undefined as string | undefined,
    gradient: 'conic-gradient(from 40deg, oklch(0.78 0.11 195), oklch(0.66 0.15 245), oklch(0.45 0.15 255))',
    education: [{ name: 'Politecnico di Milano', crest: '/team/crests/polimi.png' }],
  },
];

export default function TeamPage() {
  return (
    <>
      {/* Founders */}
      <section className="relative isolate overflow-hidden pt-36 pb-24 md:pt-44 md:pb-32">
        <div
          aria-hidden
          className="absolute inset-0 -z-10"
          style={{
            background:
              'radial-gradient(50% 50% at 50% -10%, color-mix(in oklch, var(--primary) 18%, transparent), transparent 70%)',
          }}
        />
        <Container className="relative">
          <Badge variant="soft">· Team</Badge>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="mt-6 max-w-2xl text-pretty text-[18px] leading-[1.55] text-fg-2"
          >
            BOSS è fondata da due ragazzi abruzzesi che, dopo anni fuori, hanno scelto di tornare per
            restituire qualcosa alla propria terra — con la stessa ambizione con cui inseguivano i loro
            progetti altrove.
          </motion.p>

          <Kicker index="01" label="I fondatori" className="mt-16" />
          <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-2">
            {FOUNDERS.map((f, i) => (
              <motion.div
                key={f.name}
                className="h-full"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.4 }}
                transition={{ delay: i * 0.1, duration: 0.7 }}
              >
                <Card className="flex h-full flex-col p-8 text-left">
                  <div
                    className="h-16 w-16 overflow-hidden rounded-full border border-input bg-cover bg-center"
                    style={
                      f.photo
                        ? {
                            backgroundImage: `url(${f.photo})`,
                            backgroundSize: f.photoSize ?? 'cover',
                            backgroundPosition: f.photoPosition ?? 'center',
                          }
                        : { background: f.gradient }
                    }
                  />
                  <div className="mt-6 flex items-center gap-2.5">
                    <h3 className="boss-h1 text-[26px]">{f.name}</h3>
                    {f.linkedin && (
                      <a
                        href={f.linkedin}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={`${f.name} su LinkedIn`}
                        className="text-fg-2 transition-colors hover:text-primary"
                      >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                          <path d="M20.45 20.45h-3.56v-5.57c0-1.33-.02-3.04-1.85-3.04-1.85 0-2.14 1.45-2.14 2.94v5.67H9.34V9h3.42v1.56h.05c.48-.9 1.64-1.85 3.38-1.85 3.61 0 4.28 2.38 4.28 5.47v6.27zM5.34 7.43a2.07 2.07 0 1 1 0-4.14 2.07 2.07 0 0 1 0 4.14zM7.12 20.45H3.56V9h3.56v11.45z" />
                        </svg>
                      </a>
                    )}
                  </div>
                  <div className="mt-1 font-mono text-[11px] uppercase tracking-[0.18em] text-primary">
                    {f.role}
                  </div>
                  {f.education.length > 0 && (
                    <div className="mt-auto flex items-center gap-3 border-t border-border pt-5">
                      {f.education.map((edu) => (
                        <div
                          key={edu.name}
                          role="img"
                          aria-label={edu.name}
                          title={edu.name}
                          className="h-9 w-9 rounded-full border border-input bg-white p-1"
                          style={{
                            backgroundImage: `url(${edu.crest})`,
                            backgroundSize: 'contain',
                            backgroundPosition: 'center',
                            backgroundRepeat: 'no-repeat',
                          }}
                        />
                      ))}
                    </div>
                  )}
                </Card>
              </motion.div>
            ))}
          </div>
        </Container>
      </section>

      {/* Spontaneous applications teaser */}
      <section className="relative border-t border-border py-24 md:py-32">
        <Container>
          <motion.div
            initial={{ opacity: 0, y: 32 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="relative overflow-hidden rounded-[28px] border border-input bg-card px-8 py-16 md:px-16 md:py-20"
          >
            <div
              aria-hidden
              className="absolute inset-0"
              style={{
                background:
                  'radial-gradient(50% 80% at 10% 100%, color-mix(in oklch, var(--primary) 22%, transparent), transparent 70%)',
              }}
            />
            <div className="relative">
              <Kicker index="02" label="Chi cerchiamo" />
              <h2 className="boss-display mt-5 max-w-2xl text-balance text-[36px] md:text-[56px]">
                Le eccellenze del Sud. Un progetto internazionale. <span className="text-primary">Fatto qui.</span>
              </h2>
              <p className="mt-6 max-w-xl text-pretty text-[16px] leading-[1.55] text-fg-2">
                Non guardiamo il CV, la laurea, gli anni di esperienza. Cerchiamo tre cose sole: voglia matta
                di spaccare, voglia di apprendere, curiosità e passione vere per quello che si fa. Il resto lo
                costruiamo insieme.
              </p>
              <div className="mt-9 flex flex-wrap items-center gap-3">
                <Button asChild size="lg">
                  <Link to="/candidature">Candidati ora →</Link>
                </Button>
              </div>
            </div>
          </motion.div>
        </Container>
      </section>
    </>
  );
}
